import {
	Button,
	Dimmer,
	type Job,
	Loader,
	Modal,
	TraitementLong,
} from '@mairistem/composants';
import { formatMontant, getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import { Table } from 'rsuite-table';
import ROW_HEIGHT from '../../../../constants/feuilleSaisie/tableProps';
import type TreeRow from '../../../../interfaces/feuilleSaisie/treeRow';
import type Simulation from '../../../../interfaces/simulation/simulation';
import {
	deleteMontantsApcp,
	upsertMontants,
} from '../../../../services/simulation/montantSimulation';
import { renderTreeToggle } from '../../../../utils/treeView';
import 'rsuite-table/dist/css/rsuite-table.min.css';

export interface ImportData {
	children?: ImportData[];
	identifiantImputation?: number;
	identifiantProImputation?: number;
	key: string;
	libelle: string;
	libelleDetail?: string;
	type?: string;
	commentaire?: string;
	montant: number;
	montantRar?: number;
	montantImputationComptable: number;
	montantProposition: number;
	prop?: number;
}

interface ImportModalProps {
	data?: ImportData[];
	loading?: boolean;
	onClose: () => void;
	// Recoit le resultat du job de sauvegarde quand il se termine avec succes
	onValidate: (resultat?: string) => void;
	simulation: Simulation;
	withSubstitution?: boolean;
	hasCa?: boolean;
	hasApcp?: boolean;
}

enum ModeCalcul {
	SUBSTITUER = 0,
	CUMULER = 1,
}

const ImportTable = ({
	data,
	loading,
	onClose,
	onValidate,
	simulation,
	withSubstitution = true,
	hasCa = false,
	hasApcp = false,
}: ImportModalProps) => {
	const [loadingSave, setLoadingSave] = React.useState<boolean>(false);
	const [loadingDeleteApcp, setLoadingDeleteApcp] =
		React.useState<boolean>(false);
	const [modeCalcul, setModeCalcul] = React.useState<ModeCalcul>(
		ModeCalcul.SUBSTITUER,
	);
	const [job, setJob] = React.useState<Job>();

	const handleToggleModeCalcul = () => {
		setModeCalcul(
			modeCalcul === ModeCalcul.SUBSTITUER
				? ModeCalcul.CUMULER
				: ModeCalcul.SUBSTITUER,
		);
	};

	if (loading) {
		return (
			<div className="information">
				<Dimmer active inverted>
					<Loader size="massive" />
				</Dimmer>
			</div>
		);
	}

	const colonneA = withSubstitution ? (
		<>
			Echéance annuelle
			<br />
			(A)
		</>
	) : (
		<>
			Crédit de paiement N
			<br />
			(A)
		</>
	);

	const colonneB = withSubstitution ? (
		<>
			Crédit déjà ouvert
			<br />
			(B)
		</>
	) : (
		<>
			Prévisions déjà ouvertes
			<br />
			(B)
		</>
	);

	const colonneC = hasCa ? (
		'Reste à réaliser à appliquer'
	) : withSubstitution ? (
		<>
			Besoin estimé
			<br />
			(C = A - B)
		</>
	) : (
		<>
			Proposition à appliquer
			<br />
			(C = A - B)
		</>
	);

	const renderLibelleRow = (rowData) => (
		<div className="importLibelle">
			<div>{rowData.libelle}</div>
			<div>{rowData.libelleDetail}</div>
		</div>
	);

	const renderPropositionAppplique = (rowData: ImportData) => {
		const besoinEstime =
			(rowData?.montant ?? 0) - (rowData?.montantImputationComptable ?? 0);
		if (modeCalcul === ModeCalcul.SUBSTITUER) {
			return formatMontant(besoinEstime);
		}
		return formatMontant(besoinEstime + (rowData?.montantProposition ?? 0));
	};

	const handleSave = async () => {
		const montants = [];

		data.forEach((row) => {
			row?.children?.forEach((imputation) => {
				const besoinEstime = hasCa
					? (imputation?.montantRar ?? 0)
					: (imputation?.montant ?? 0) -
						(imputation?.montantImputationComptable ?? 0);

				const montant =
					modeCalcul === ModeCalcul.SUBSTITUER
						? besoinEstime
						: besoinEstime + (imputation?.montantProposition ?? 0);

				montants.push({
					identifiantImputation: imputation.identifiantImputation,
					identifiantProImputation: imputation?.identifiantProImputation,
					type: row.type,
					commentaire: row.libelle,
					...(hasCa ? { rar: montant } : { prop: montant }),
				});
			});
		});

		try {
			setLoadingSave(true);
			const newJob = await upsertMontants(
				simulation?.identifiant,
				montants,
				hasApcp,
			);
			setJob(newJob);
		} catch (_e) {
			toast.error('Erreur lors de la sauvegarde des propositions');
			setLoadingSave(false);
			onClose();
		}
	};

	const handleDeleteApcp = async () => {
		try {
			setLoadingDeleteApcp(true);

			await deleteMontantsApcp(simulation?.identifiant);
			onValidate();
		} catch (e) {
			toast.error(
				getErrorMessage(
					e,
					'Erreur lors de la suppression des propositions liés aux Apcp',
				),
			);
		} finally {
			setLoadingDeleteApcp(false);
			onClose();
		}
	};

	const handleFinishJob = (finishedJob: Job) => {
		setJob(null);

		if (finishedJob.status === 1) {
			onValidate(finishedJob.resultat);
		} else {
			toast.error(finishedJob.resultat);
			setLoadingSave(false);
			onClose();
		}
	};

	const handleErrorJob = () => {
		setJob(null);
		toast.error('Erreur lors du traitement');
		setLoadingSave(false);
		onClose();
	};

	return (
		<>
			<div className="import">
				{withSubstitution && (
					<div className="importEmpruntChoice">
						<Button.Group>
							<Button
								positive={modeCalcul === ModeCalcul.SUBSTITUER}
								onClick={handleToggleModeCalcul}
							>
								Substituer les besoins estimés aux propositions déjà saisies
							</Button>
							<Button.Or text="ou" />
							<Button
								positive={modeCalcul === ModeCalcul.CUMULER}
								onClick={handleToggleModeCalcul}
							>
								Cumuler les besoins estimés aux propositions déjà saisies
							</Button>
						</Button.Group>
					</div>
				)}
				<div className="importTableWrapper">
					<Table
						className="importTable"
						data={data}
						hover={false}
						isTree
						locale={{ emptyMessage: 'Aucune donnée', loading: 'Chargement' }}
						rowKey="key"
						rowClassName="imputationTableRow"
						rowHeight={ROW_HEIGHT}
						virtualized
						wordWrap={false}
						headerHeight={60}
						autoHeight
						renderTreeToggle={(_expandButton, rowData, isExpanded) =>
							renderTreeToggle(rowData as unknown as TreeRow, isExpanded, '')
						}
					>
						{({ Cell, Column, HeaderCell }) => (
							<>
								<Column flexGrow={4} fullText verticalAlign="middle">
									<HeaderCell>
										{`Ligne budgétaire ${simulation?.exercice?.anneeExercice}`}
									</HeaderCell>
									{/* Sans nowrap, un libelle trop long passe sous le chevron de depliage */}
									<Cell dataKey="libelle" style={{ flexWrap: 'nowrap' }}>
										{(rowData) => renderLibelleRow(rowData)}
									</Cell>
								</Column>

								{!hasCa && (
									<>
										<Column
											flexGrow={2}
											fullText
											verticalAlign="middle"
											align="right"
										>
											<HeaderCell>{colonneA}</HeaderCell>
											<Cell dataKey="montant">
												{(rowData) => formatMontant(rowData?.montant)}
											</Cell>
										</Column>

										<Column
											flexGrow={2}
											fullText
											verticalAlign="middle"
											align="right"
										>
											<HeaderCell>{colonneB}</HeaderCell>
											<Cell dataKey="montantImputationComptable">
												{(rowData) =>
													formatMontant(rowData?.montantImputationComptable)
												}
											</Cell>
										</Column>
									</>
								)}

								<Column
									flexGrow={2}
									fullText
									verticalAlign="middle"
									align="right"
								>
									<HeaderCell>{colonneC}</HeaderCell>
									<Cell dataKey="besoinEstime">
										{(rowData) =>
											formatMontant(
												hasCa
													? (rowData?.montantRar ?? 0)
													: (rowData?.montant ?? 0) -
															(rowData?.montantImputationComptable ?? 0),
											)
										}
									</Cell>
								</Column>

								{withSubstitution && (
									<>
										<Column
											flexGrow={2}
											fullText
											verticalAlign="middle"
											align="right"
										>
											<HeaderCell>
												Proposition déjà saisie
												<br />
												(D)
											</HeaderCell>
											<Cell dataKey="montantProposition">
												{(rowData) =>
													formatMontant(rowData?.montantProposition)
												}
											</Cell>
										</Column>
										<Column
											flexGrow={2}
											fullText
											verticalAlign="middle"
											align="right"
										>
											<HeaderCell>
												Proposition à appliquer
												<br />
												{modeCalcul === ModeCalcul.SUBSTITUER
													? '(E = C)'
													: '(E = C + D)'}
											</HeaderCell>
											<Cell dataKey="montantPropositionApplique">
												{(rowData) => renderPropositionAppplique(rowData)}
											</Cell>
										</Column>
									</>
								)}
							</>
						)}
					</Table>
				</div>
			</div>

			<Modal.Actions className="importActions">
				{hasApcp && (
					<Button
						content="Supprimer les montants liés aux APCP"
						positive
						onClick={handleDeleteApcp}
						loading={loadingDeleteApcp}
					/>
				)}
				<Button
					content="Appliquer ces montants"
					positive
					onClick={handleSave}
					loading={loadingSave}
				/>
			</Modal.Actions>

			{job && (
				<TraitementLong
					identifiantJob={job.identifiant}
					onError={handleErrorJob}
					onFinish={handleFinishJob}
					title="Sauvegarde des propositions en cours"
					indeterminate
				/>
			)}
		</>
	);
};

export default ImportTable;
